import { makeBooleanDataField, makeInt64DataField } from "@microsoft/oteljs";
/**
 * Maximum allowed serialized payload size in bytes for a single telemetry event.
 * Set conservatively below the OneCollector per-request server limit (~2-4MB) so that a single
 * oversized event never causes an HTTP 413 (Content Too Large) rejection. Kept consistent with
 * the reactive summary cap ({@link MaxSummaryPayloadSizeBytes} = 50KB) in OversizedEventHandler.
 */ export const MaxEventPayloadSizeBytes = 65536; // 64KB
/**
 * Marker appended to any string field value that gets truncated by {@link capOversizedDataFields}.
 */ export const TruncationMarker = "...[truncated]";
/**
 * Minimum length a string field value is allowed to keep while being truncated. Values are not cut
 * below this length so that the most important leading context (error type, first stack frame, etc.)
 * is preserved even for very large events.
 */ const MinTruncatedValueLength = 50;
/**
 * Estimated bytes of JSON structure overhead per field (quotes, colons, commas, type wrapper).
 */ const PerFieldStructureOverhead = 12;
/**
 * Conservative estimate of the serialized bytes taken by the *values* of the two marker fields
 * appended after truncation: `WasTruncatedPreSend` (a boolean, ~4 bytes) and
 * `OriginalPayloadSizeBytes` (an int64 rendered as up to ~20 digits), with margin. The field
 * *names* and per-field structure overhead are accounted for separately in {@link capOversizedDataFields}.
 */ const MarkerValuesSizeEstimateBytes = 40;
/**
 * Estimates the serialized size, in bytes, of a telemetry event's data fields.
 * Uses TextEncoder for accurate UTF-8 byte counting (handles non-ASCII characters correctly).
 * @param fields Data fields to measure
 * @returns Estimated size in bytes
 */ export function estimateDataFieldsSizeBytes(fields) {
    let total = 0;
    for (const field of fields){
        total += byteLength(field.name) + byteLength(String(field.value)) + PerFieldStructureOverhead;
    }
    return total;
}
/**
 * Caps the size of an oversized telemetry event's payload before it is sent to OneDS.
 *
 * OneCollector rejects any single request whose body exceeds its server limit with HTTP 413
 * (Content Too Large); a single oversized event (which cannot be split across requests) is the
 * classic trigger — e.g. large error objects, full API response bodies, or stack traces logged
 * when opening a premium plan. To PREVENT the 413 (rather than only react to it after the fact,
 * which is what {@link createOversizedEventHandler} does), this function truncates the largest
 * string field values until the estimated payload is under {@link MaxEventPayloadSizeBytes}.
 *
 * The common case (payload already under the limit) returns the original array unchanged with
 * effectively zero overhead. When truncation occurs, two marker fields are appended so it can be
 * measured in telemetry: `WasTruncatedPreSend` and `OriginalPayloadSizeBytes`.
 *
 * @param fields The event's data fields
 * @param options Optional overrides (e.g. custom size limit)
 * @returns The original array if within the limit, otherwise a new array with truncated values
 */ export function capOversizedDataFields(fields, options) {
    if (!fields || fields.length === 0) {
        return fields;
    }
    const maxSize = options?.maxPayloadSizeBytes ?? MaxEventPayloadSizeBytes;
    const originalSize = estimateDataFieldsSizeBytes(fields);
    if (originalSize <= maxSize) {
        return fields;
    }
    // Work on a shallow copy so callers' fields are never mutated.
    const result = fields.map((field)=>({
            ...field
        }));
    // Reserve room for the two marker fields we append below so the final payload (fields + markers)
    // stays under the configured limit.
    const markerOverhead = byteLength("WasTruncatedPreSend") + byteLength("OriginalPayloadSizeBytes") + MarkerValuesSizeEstimateBytes + 2 * PerFieldStructureOverhead;
    const maxFieldsSize = Math.max(0, maxSize - markerOverhead);
    // Repeatedly halve the currently-largest string value until the fields fit within the
    // marker-reserved budget (or nothing more can be reduced). Halving guarantees quick convergence
    // for a small number of huge fields, and findLargestTruncatableField stops returning fields once
    // they reach the truncation floor.
    while(estimateDataFieldsSizeBytes(result) > maxFieldsSize){
        const largest = findLargestTruncatableField(result);
        if (largest == null) {
            // Nothing left to shrink (all string fields are already at the truncation floor). Return
            // the best-effort result without appending markers, which would only make it larger.
            return result;
        }
        const currentValue = result[largest].value;
        const withoutMarker = stripTruncationMarker(currentValue);
        // Reserve space for the truncation marker so truncation never increases the string length.
        const targetTotalLength = Math.max(Math.floor(withoutMarker.length / 2), MinTruncatedValueLength);
        const retainedLength = Math.max(0, Math.min(withoutMarker.length, targetTotalLength - TruncationMarker.length));
        const nextValue = withoutMarker.substring(0, retainedLength) + TruncationMarker;
        if (nextValue.length >= currentValue.length) {
            // The largest field cannot be shrunk any further; stop to avoid an infinite loop and
            // return best-effort without markers.
            return result;
        }
        // Mutate the value in place to preserve the field's discriminated DataField type.
        result[largest].value = nextValue;
    }
    result.push(makeBooleanDataField("WasTruncatedPreSend", true));
    result.push(makeInt64DataField("OriginalPayloadSizeBytes", originalSize));
    return result;
}
/**
 * Finds the index of the string field with the longest value that is still above the truncation
 * floor. Returns null if no such field exists (i.e. nothing left to shrink).
 */ function findLargestTruncatableField(fields) {
    let largestIndex = null;
    let largestLength = MinTruncatedValueLength;
    for(let i = 0; i < fields.length; i++){
        const value = fields[i].value;
        if (typeof value === "string") {
            const length = stripTruncationMarker(value).length;
            if (length > largestLength) {
                largestLength = length;
                largestIndex = i;
            }
        }
    }
    return largestIndex;
}
/** Removes a trailing {@link TruncationMarker} if present so values are not marked twice. */ function stripTruncationMarker(value) {
    return value.endsWith(TruncationMarker) ? value.substring(0, value.length - TruncationMarker.length) : value;
}
/** Shared encoder reused across calls to avoid per-call allocation on the telemetry hot path. */ const sharedTextEncoder = (()=>{
    try {
        return new TextEncoder();
    } catch  {
        return undefined;
    }
})();
/** Returns the UTF-8 byte length of a string, falling back to `.length` if TextEncoder is unavailable. */ function byteLength(value) {
    /* istanbul ignore next -- defensive fallback for runtimes without TextEncoder (unreachable in supported environments) */ return sharedTextEncoder ? sharedTextEncoder.encode(value).length : value.length;
}
